/**
 * Every word the exercise move says — the ⋯ entry that lifts, the lift's running commentary, and
 * the sentence the dock's live region reads after a drop.
 *
 * ── The ⋯ carries no WITHIN-DAY move entries, and "Przenieś do dnia…" is back (7 Sep 2026) ───
 * It used to carry "Przenieś w górę / w dół" and a "Przenieś do dnia…" group, and the owner cut both
 * on 3 August 2026: moving is drag and drop's job and the keyboard lift — arrows, then Space —
 * dispatches the identical `moveExercise` command a drop does. The lift started from a grip until
 * 15 September 2026, when the owner removed the grip; it starts from the ⋯'s "Przestaw" entry now,
 * which is a way INTO the lift and not a reorder of its own.
 *
 * That reasoning holds for the reorder and NOT for the cross-day move, which is why one of the two
 * came back. The lift steps a block up and down inside its own day; the pointer can also drop a
 * block on another day's column of the week behind the panel, and `exerciseMovedToDay` is the
 * sentence that lands when it does. There was no keyboard or menu route to that at all — a whole
 * mechanic for pointers only, which view ADR §12 forbids outright. The group dispatches the same
 * `runMoveCommand` the drop does; it is not a reduced fallback.
 *
 * The mechanic is `entities/plan-draft`'s: `moveCommands` decides what a drop means, `dropGates`
 * decides whether it may happen, and both answer in facts. This is where those facts become Polish.
 * The week canvas words the SAME union again for the session drag (`pages/plan-builder/model/
 * dayMove.ts`), and the duplication is deliberate — "ta faza jest zamknięta" is one fact about two
 * different things, and a string vague enough to cover a session card and an exercise row at once
 * would be worse than either.
 *
 * ── The cross-day sentence says what the domain does, because nothing else will ──────────────
 * View ADR §11 case 1: moving an exercise between days is "removal plus addition. The slot is not
 * carried across, so the exercise's activation cycle and dynamic chain restart (R12) — **the UI
 * states this on drop**, because it looks like a move and behaves like a first exposure." That is
 * the only place in this app where a drag has a consequence the trainer cannot see, so it is the one
 * announcement that carries a clause about the model rather than about the position.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type { AuthorableExerciseRole, DropRefusal, MoveOutcome } from '~/entities/plan-draft';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import type { Translate } from './dayProgramming';

const KEY = 'web:planBuilder.dnd';

/**
 * One day an exercise can land in — the day arrows' destinations, and what a drop on the week
 * behind the panel resolves back through. Built by the HOST.
 *
 * The days of a phase are the WEEK's answer, not the dock's: the dock renders one day out of the
 * document and has no phase. So the page composes the list and this feature words nothing about it.
 */
export interface DayMoveTarget {
  readonly slotToken: string;
  /**
   * 1 (Monday) – 7 (Sunday) — what a drop on the WEEK GRID behind the panel resolves back through —
   * and `null` on a self-paced plan, whose days carry no weekday. The tape draws no weekday target at
   * all, so a `null` here simply never matches and the drop resolves to nothing, which is the same
   * answer a rest day gives.
   */
  readonly weekday: number | null;
  /** "Środa — Pull A", already worded by the host. The entry's accessible name and announcement. */
  readonly label: string;
  /**
   * "Środa", "D3" — the day's identity with the workout standing on it stripped off, and what the ⋯
   * PRINTS since 8 September 2026 (owner: the menu was a wall of text).
   *
   * A weekday identifies a day of a phase on its own — a date-based phase holds at most one day per
   * weekday — and a tape day's badge does the same, so nothing about WHICH day is lost by dropping
   * the session name from the row. It is not lost from the menu either: `label` is still the entry's
   * accessible NAME, and it contains this string, which is what WCAG 2.5.3 asks of the pair.
   */
  readonly shortLabel: string;
  /**
   * How many main-work blocks the day holds. A cross-day drop APPENDS, and the append index is the
   * count — the week card shows no positions to land between, so deriving one from the pointer's
   * height over a card that draws none would be a guess dressed as precision.
   */
  readonly blockCount: number;
}

export function exerciseRefusalWords(t: Translate, reason: DropRefusal): string {
  return t(`${KEY}.refusal.${reason}`);
}

/** One entry of the ⋯'s "Przenieś do dnia…" group. */
export interface ExerciseDayTargetView {
  readonly slotToken: string;
  /** What the row PRINTS — a bare weekday or day badge. See `DayMoveTarget.shortLabel`. */
  readonly label: string;
  /** The entry's accessible name, which contains `label` and adds the workout standing there. */
  readonly name: string;
  /** Why this stop cannot act — the day the exercise is already in. `null` when it can. */
  readonly disabledReason: string | null;
  /** Where the insert lands: a cross-day move APPENDS, so the target day's block count. */
  readonly blockCount: number;
}

/** The ⋯'s move group, worded. */
export interface ExerciseMoveMenuView {
  readonly label: string;
  readonly targets: readonly ExerciseDayTargetView[];
  /**
   * Why the WHOLE group cannot act — a consumed phase, or a phase holding this day alone. `null`
   * when it can. It is asked once for the group rather than repeated on every stop, exactly as
   * `SessionMenu`'s `disabledReason` is.
   */
  readonly disabledReason: string | null;
}

/**
 * The days this exercise can be sent to, worded — the menu twin of the drop on the week behind the
 * panel.
 *
 * The current day keeps its entry, disabled and carrying its reason, rather than being dropped:
 * view ADR §12 wants a refused control focusable with its explanation, and a menu whose entries
 * appear and disappear as the dock steps between days is one a keyboard user cannot learn. The same
 * shape the session card's own weekday group already has.
 *
 * Every stop PRINTS the day's bare identity and is NAMED with the workout as well — see
 * `DayMoveTarget.shortLabel` for why the two are split.
 */
export function exerciseMoveMenu(
  t: Translate,
  daySlotToken: string,
  dayTargets: readonly DayMoveTarget[],
): ExerciseMoveMenuView {
  const others = dayTargets.filter((target) => target.slotToken !== daySlotToken);
  return {
    label: t(`${KEY}.exerciseMoveToDay`),
    disabledReason: others.length === 0 ? t(`${KEY}.exerciseNoOtherDay`) : null,
    targets: dayTargets.map((target) => ({
      slotToken: target.slotToken,
      blockCount: target.blockCount,
      // The day the exercise is already in prints exactly what every other stop prints. It used to
      // print "poniedziałek — Push A — obecny dzień", which spelled its own refused state into the
      // row and made the group the widest thing in the menu. The state is still carried, twice: as
      // `aria-disabled`, and as the reason on `aria-describedby`.
      label: target.shortLabel,
      name: target.label,
      disabledReason:
        target.slotToken === daySlotToken ? t(`${KEY}.exerciseDayCurrentReason`) : null,
    })),
  };
}

/**
 * What a cross-day move is about to cost the client, or `null` when there is nothing to ask.
 *
 * ── Why this one move asks and the reorder beside it does not ────────────────────────────────
 * Moving a block to ANOTHER DAY mints a fresh slot token (`draftOperations`' `transplantedExercise`),
 * so the backend reads the edit as a removal plus an addition. `plan-progression-requirements.md`
 * R3 spells out the consequence in its own words — retiring an exercise and adding it back is a NEW
 * slot — and a new slot is prescribed the authored base: the client's lived results do not follow
 * it. The token is minted deliberately and stays minted; preserving it would instead offer the
 * backend a slot in a day that does not run in the lived cycles, which is what `plan_window_locked`
 * refuses. So the act is right and its SILENCE was the defect.
 *
 * Three cases answer `null`, and each is a fact rather than a leniency:
 *
 * · a LIBRARY template, which has no client downstream and nothing to restart;
 * · a WITHIN-DAY reorder, which carries the same slot token and changes no chain at all;
 * · a target that is the day the block is already in — the menu keeps that stop, refused, and a
 *   gesture that lands back where it started is not a move.
 *
 * It names all three things the trainer needs: the movement (as the prompt's own subject, which is
 * what keeps a Polish name in the nominative), the day it is going to, and what happens to the
 * progression. The words are this app's own — mobile has no such confirm, because mobile has no
 * cross-day move.
 */
export function crossDayMoveConfirm(
  t: Translate,
  facts: {
    /** A running client fork. `false` on a library template. */
    readonly isLivePlan: boolean;
    readonly sourceDaySlotToken: string;
    readonly targetDaySlotToken: string;
    readonly name: string;
    /**
     * "Środa", "D3" — the target day's bare identity, as `DayMoveTarget.shortLabel` words it.
     *
     * The SHORT form, not the "środa: Pull A" the menu names its stops with: the sentence already
     * carries a colon of its own, and a weekday identifies a day of a phase on its own — a
     * date-based phase holds at most one day per weekday.
     */
    readonly targetDayLabel: string;
  },
): ConfirmPrompt | null {
  if (!facts.isLivePlan) return null;
  if (facts.sourceDaySlotToken === facts.targetDaySlotToken) return null;
  return {
    title: t(`${KEY}.crossDayConfirm.title`),
    subject: facts.name,
    // The day is named in the DESCRIPTION, after a colon, and not inflected into the question:
    // Polish would want a case the app cannot compute from a label, which is the same reason
    // `ConfirmPrompt.subject` exists for the movement's own name.
    description: t(`${KEY}.crossDayConfirm.body`, { day: facts.targetDayLabel }),
    confirmLabel: t(`${KEY}.crossDayConfirm.confirm`),
    cancelLabel: t(`${KEY}.crossDayConfirm.cancel`),
    // The client's progression on this movement does not survive the move and no answer here keeps
    // it, so the confirm wears the same `--danger` fill every other consequential prompt in the
    // builder does. Focus stays on Anuluj, which is the dialog's own rule.
    destructiveConfirm: true,
  };
}

/** "Przenieś ćwiczenie X" — the name the block's row takes while it holds a keyboard lift. */
export function exerciseLiftLabel(t: Translate, name: string): string {
  return t(`${KEY}.exerciseGrip`, { name });
}

/**
 * The ⋯'s "Przestaw" entry, which starts the keyboard lift now that the row draws no grip (owner,
 * 15 September 2026).
 *
 * It refuses rather than disappears, carrying the reason, in one case: a day holding this block
 * alone, where every arrow would stop where it started. A day the client has already trained is not
 * a refusal (owner, 15 September 2026).
 */
export function exerciseLiftEntry(
  t: Translate,
  blockCount: number,
): { readonly label: string; readonly disabledReason: string | null } {
  return {
    label: t(`${KEY}.exerciseLiftEntry`),
    disabledReason: blockCount <= 1 ? t(`${KEY}.exerciseLiftAlone`) : null,
  };
}

/** What the live region says the moment a keyboard lift picks a block up. Said once. */
export function exerciseLiftedWords(
  t: Translate,
  name: string,
  position: number,
  total: number,
): string {
  return t(`${KEY}.exerciseLifted`, { name, position: position + 1, total });
}

/** What it says on each subsequent arrow — the position alone, with no instruction to wade through. */
export function exercisePositionWords(t: Translate, position: number, total: number): string {
  return t(`${KEY}.exercisePreview`, { position: position + 1, total });
}

/**
 * What the dock's live region says once an exercise move has been dispatched.
 *
 * `unchanged` is worded rather than left silent: a trainer who dropped a block back where it started
 * needs to be told nothing happened, or a working control reads as a broken one.
 */
export function exerciseMoveAnnouncement(
  t: Translate,
  outcome: MoveOutcome,
  context: {
    readonly name: string;
    /** The weekday the exercise landed in — only used by the cross-day sentence. */
    readonly targetDayLabel: string;
    /**
     * Which block a library insert landed in. `Main` for every other outcome: a block dropped on
     * the warm-up zone has an outcome of its own (`movedToWarmup`), so it needs no role here.
     *
     * It is asked for rather than inferred, because the two cases cannot be told apart from the
     * numbers. A warm-up insert reports the main work's block count as both its position and its
     * total, and so does a main insert appended to an empty day; this branched on `total === 0`
     * until 10 August 2026 and would have announced "Dodano Rower na pozycję 3 z 3" for a warm-up
     * on any day that already prescribed something — a position in a list the movement is not in.
     */
    readonly role: AuthorableExerciseRole;
  },
): string {
  if (outcome.kind === 'refused') return exerciseRefusalWords(t, outcome.reason);
  if (outcome.kind === 'unchanged') return t(`${KEY}.cancelled`);
  if (outcome.kind === 'inserted') {
    return context.role === 'Warmup'
      ? t(`${KEY}.insertedWarmup`, { name: context.name })
      : t(`${KEY}.inserted`, {
          name: context.name,
          position: outcome.position,
          total: outcome.total,
        });
  }
  if (outcome.kind === 'movedToWarmup') {
    return t(`${KEY}.exerciseMovedToWarmup`, { name: context.name });
  }
  if (outcome.kind !== 'movedExercise') return '';
  return outcome.reAnchored
    ? t(`${KEY}.exerciseMovedToDay`, {
        name: context.name,
        day: context.targetDayLabel,
        position: outcome.position,
        total: outcome.total,
      })
    : t(`${KEY}.exerciseMoved`, { position: outcome.position, total: outcome.total });
}
